/**
 * Template Data Transformers
 * Functions to transform template data between different formats
 */

import {
  extractVariablesFromTemplateJson,
  renumberVariables,
  extractVariableNumbers,
} from "./templateUtils";

/**
 * Transform duplicate template data to component format
 * Handles both API template format (with template_json) and flat format
 */
export const transformDuplicateTemplate = (duplicateData) => {
  // If it's an API template format (has template_json), extract from it
  if (duplicateData.template_json && duplicateData.template_json.components) {
    const templateJson = duplicateData.template_json;
    const components = templateJson.components || [];

    // Extract components directly from template_json
    const headerComponent = components.find(c => c.type === 'HEADER');
    const bodyComponent = components.find(c => c.type === 'BODY');
    const footerComponent = components.find(c => c.type === 'FOOTER');
    const buttonsComponent = components.find(c => c.type === 'BUTTONS');

    // Build components array with IDs for buttons
    const transformedComponents = [];

    // HEADER component
    if (headerComponent) {
      transformedComponents.push({
        type: "HEADER",
        format: headerComponent.format || "NONE",
        text: headerComponent.text || "",
      });
    }

    // BODY component
    if (bodyComponent) {
      transformedComponents.push({
        type: "BODY",
        text: bodyComponent.text || "",
        example: null,
      });
    }

    // FOOTER component
    if (footerComponent) {
      transformedComponents.push({
        type: "FOOTER",
        text: footerComponent.text || "",
      });
    }

    // BUTTONS component with IDs
    if (buttonsComponent && buttonsComponent.buttons && buttonsComponent.buttons.length > 0) {
      const buttonsWithIds = buttonsComponent.buttons.map((btn, index) => {
        const buttonId = Date.now() + index * 10000;
        const buttonObj = {
          id: buttonId,
          type: btn.type,
          section: btn.type === "QUICK_REPLY" ? "QUICK_REPLY" : "CTA",
          text: btn.text || "",
          url: btn.url || undefined,
          phone_number: btn.phone_number || undefined,
          example: btn.example || undefined,
        };

        // Extract URL variable if URL contains {{variable}}
        if (btn.type === "URL" && btn.url) {
          const urlVariables = (btn.url || "").match(/\{\{(\d+)\}\}/g) || [];
          if (urlVariables.length === 1) {
            const varMatch = urlVariables[0].match(/\{\{(\d+)\}\}/);
            if (varMatch) {
              const sample = Array.isArray(btn.example) && btn.example.length > 0
                ? btn.example[0]
                : "";
              buttonObj.urlVariable = {
                name: varMatch[1],
                sample: sample,
              };
            }
          }
        }

        return buttonObj;
      });

      transformedComponents.push({
        type: "BUTTONS",
        buttons: buttonsWithIds,
      });
    }

    // Extract variables from template_json
    const variables = extractVariablesFromTemplateJson(templateJson);

    // Get header file preview
    const headerFormat = headerComponent?.format?.toUpperCase() || "NONE";
    const headerFilePreview =
      ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat) &&
      duplicateData.header_media_url
        ? duplicateData.header_media_url
        : null;

    return {
      name: duplicateData.name || `copy_${duplicateData.originalName || duplicateData.name || ""}`,
      category: duplicateData.category || "UTILITY",
      language: duplicateData.language || "en_US",
      components: transformedComponents,
      wabaId: duplicateData.waba_id || duplicateData.wabaId || "",
      phoneId: duplicateData.phone_id || duplicateData.phoneId || "",
      headerFile: null,
      headerFilePreview,
      header_media_url: duplicateData.header_media_url || null,
      variables: variables,
      parameter_format: duplicateData.parameter_format || "numbered",
    };
  }

  // Handle flat format (existing logic)
  const components = [];

  // HEADER component
  if (duplicateData.headerFormat && duplicateData.headerFormat !== "NONE") {
    const headerComponent = {
      type: "HEADER",
      format: duplicateData.headerFormat,
    };
    if (duplicateData.headerText) {
      headerComponent.text = duplicateData.headerText;
    }
    components.push(headerComponent);
  }

  // BODY component
  components.push({
    type: "BODY",
    text: duplicateData.body || "",
    example: null,
  });

  // FOOTER component
  if (duplicateData.footer) {
    components.push({
      type: "FOOTER",
      text: duplicateData.footer,
    });
  }

  // BUTTONS component
  if (duplicateData.buttons && duplicateData.buttons.length > 0) {
    const buttonsWithIds = duplicateData.buttons.map((btn, index) => {
      const buttonId =
        btn.id && typeof btn.id === "number"
          ? btn.id
          : Date.now() + index * 10000;

      const buttonObj = {
        id: buttonId,
        type: btn.type,
        section: btn.type === "QUICK_REPLY" ? "QUICK_REPLY" : "CTA",
        text: btn.text || "",
        url: btn.url || undefined,
        phone_number: btn.phone_number || undefined,
        example: btn.example || undefined,
      };

      // Extract URL variable if URL contains {{variable}}
      if (btn.type === "URL" && btn.url) {
        const urlVariables = (btn.url || "").match(/\{\{(\d+)\}\}/g) || [];
        if (urlVariables.length === 1) {
          const varMatch = urlVariables[0].match(/\{\{(\d+)\}\}/);
          if (varMatch) {
            const sample =
              btn.urlVariable?.sample ||
              (Array.isArray(btn.example) && btn.example.length > 0
                ? btn.example[0]
                : "");
            buttonObj.urlVariable = {
              name: varMatch[1],
              sample: sample,
            };
          }
        } else if (urlVariables.length === 0 && btn.urlVariable) {
          buttonObj.urlVariable = btn.urlVariable;
        }
      }

      return buttonObj;
    });

    components.push({
      type: "BUTTONS",
      buttons: buttonsWithIds,
    });
  }

  // Extract variables from template_json if provided
  const variables = duplicateData.template_json
    ? extractVariablesFromTemplateJson(duplicateData.template_json)
    : [];

  // Merge with existing variables if any
  const existingVariables = duplicateData.variables || [];
  const mergedVariables = existingVariables.map((existingVar) => {
    const jsonVar = variables.find(
      (v) => v.name === existingVar.name && v.target === existingVar.target
    );
    const finalSample = jsonVar?.sample || existingVar.sample || "";
    return {
      ...existingVar,
      sample: finalSample,
    };
  });

  // Add new variables from template_json
  variables.forEach((jsonVar) => {
    const exists = mergedVariables.some(
      (v) => v.name === jsonVar.name && v.target === jsonVar.target
    );
    if (!exists) {
      mergedVariables.push(jsonVar);
    }
  });

  // Update button URL variable samples
  if (duplicateData.buttons && duplicateData.buttons.length > 0) {
    const buttonsComponent = components.find((c) => c.type === "BUTTONS");
    if (buttonsComponent && buttonsComponent.buttons) {
      const buttonUrlVariableSamples = {};
      const templateJson =
        typeof duplicateData.template_json === "string"
          ? JSON.parse(duplicateData.template_json)
          : duplicateData.template_json || {};
      const jsonButtons = templateJson.components?.find((c) => c.type === "BUTTONS");

      if (jsonButtons?.buttons) {
        jsonButtons.buttons.forEach((btn) => {
          if (btn.type === "URL" && btn.url) {
            const urlVariables = (btn.url || "").match(/\{\{(\d+)\}\}/g) || [];
            urlVariables.forEach((match) => {
              const varNum = match.replace(/[{}]/g, "");
              const sample =
                Array.isArray(btn.example) && btn.example.length > 0
                  ? btn.example[0]
                  : "";
              if (sample) {
                buttonUrlVariableSamples[varNum] = sample;
              }
            });
          }
        });
      }

      buttonsComponent.buttons.forEach((btn) => {
        if (btn.type === "URL" && btn.urlVariable) {
          const varNum = btn.urlVariable.name;
          if (buttonUrlVariableSamples[varNum]) {
            btn.urlVariable.sample = buttonUrlVariableSamples[varNum];
          }
        }
      });
    }
  }

  // Get header file preview
  const headerFilePreview =
    ["IMAGE", "VIDEO", "DOCUMENT"].includes(duplicateData.headerFormat) &&
    duplicateData.header_media_url
      ? duplicateData.header_media_url
      : null;

  return {
    name: duplicateData.name || `copy_${duplicateData.originalName || ""}`,
    category: duplicateData.category || "UTILITY",
    language: duplicateData.language || "en_US",
    components,
    wabaId: duplicateData.wabaId || "",
    phoneId: duplicateData.phoneId || "",
    headerFile: null,
    headerFilePreview,
    header_media_url: duplicateData.header_media_url || null,
    variables: mergedVariables.length > 0 ? mergedVariables : variables,
    parameter_format: duplicateData.parameter_format || "numbered",
  };
};

const creatorId = (prefix, index) => `${prefix}_${Date.now()}_${index}`;

const splitE164 = (e164) => {
  const cleaned = String(e164 || "").replace(/[^\d+]/g, "");
  // Try +<cc><national>, assume cc is 1-3 digits.
  const m = cleaned.match(/^\+(\d{1,3})(\d{4,})$/);
  if (!m) return { country_code: "+91", phone_number: cleaned.replace(/\D/g, "") };
  return { country_code: `+${m[1]}`, phone_number: m[2] };
};

const creatorMediaSampleFromHeaderFormat = (format) => {
  const f = String(format || "").toUpperCase();
  if (f === "IMAGE") return "image";
  if (f === "VIDEO") return "video";
  if (f === "DOCUMENT") return "document";
  if (f === "LOCATION") return "location";
  return "none";
};

const detectCreatorVars = (text, variableType) => {
  if (!text) return [];
  if (variableType === "named") {
    const matches =
      (text || "").match(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g) || [];
    return matches.map((m) => m.replace(/[{}]/g, ""));
  }
  const matches = (text || "").match(/\{\{(\d+)\}\}/g) || [];
  const nums = matches
    .map((m) => parseInt(m.replace(/[{}]/g, ""), 10))
    .filter((n) => !isNaN(n) && n > 0);
  return [...new Set(nums)].sort((a, b) => a - b).map((n) => String(n));
};

const headerSampleMapFromExample = (headerExample, variableType) => {
  if (!headerExample) return {};
  if (variableType === "named") {
    const arr = headerExample.header_text_named_params;
    if (!Array.isArray(arr)) return {};
    const map = {};
    arr.forEach((p) => {
      if (p?.param_name) map[String(p.param_name)] = String(p.example || "");
    });
    return map;
  }
  const arr = headerExample.header_text;
  if (!Array.isArray(arr) || arr.length === 0) return {};
  // Header supports only one variable in this UI; Meta provides samples as array.
  return { "1": String(arr[0] || "") };
};

const bodySampleMapFromExample = (bodyExample, variableType) => {
  if (!bodyExample) return {};
  if (variableType === "named") {
    const arr = bodyExample.body_text_named_params;
    if (!Array.isArray(arr)) return {};
    const map = {};
    arr.forEach((p) => {
      if (p?.param_name) map[String(p.param_name)] = String(p.example || "");
    });
    return map;
  }
  const arr = bodyExample.body_text;
  const firstRow = Array.isArray(arr) && Array.isArray(arr[0]) ? arr[0] : null;
  if (!firstRow) return {};
  const map = {};
  firstRow.forEach((sample, idx) => {
    map[String(idx + 1)] = String(sample || "");
  });
  return map;
};

/**
 * Transform duplicate template data into TemplateCreator state shape.
 * Used by `/templates/create` which renders `TemplateCreator`.
 */
export const transformDuplicateTemplateToCreatorState = (duplicateData) => {
  const templateJson =
    duplicateData?.template_json && typeof duplicateData.template_json === "string"
      ? JSON.parse(duplicateData.template_json)
      : duplicateData?.template_json || {};

  const components = Array.isArray(templateJson.components) ? templateJson.components : [];
  const header = components.find((c) => c.type === "HEADER");
  const body = components.find((c) => c.type === "BODY");
  const footer = components.find((c) => c.type === "FOOTER");
  const buttons = components.find((c) => c.type === "BUTTONS");

  const headerFormat = header?.format || "NONE";
  const mediaSample = creatorMediaSampleFromHeaderFormat(headerFormat);
  const isHeaderText = String(headerFormat || "").toUpperCase() === "TEXT";

  const format = String(
    duplicateData?.parameter_format || templateJson?.parameter_format || ""
  ).toLowerCase();
  const placeholderText = `${header?.text || ""}\n${body?.text || ""}`;
  const hasNamedPlaceholder = /\{\{[a-zA-Z_][a-zA-Z0-9_]*\}\}/.test(placeholderText);
  const hasNumberedPlaceholder = /\{\{\d+\}\}/.test(placeholderText);
  const variableType =
    format === "named" || (format !== "positional" && hasNamedPlaceholder && !hasNumberedPlaceholder)
      ? "named"
      : "numbered";

  const headerText = isHeaderText ? header?.text || "" : "";
  const bodyText = body?.text || "";

  const headerVarNames = mediaSample === "none" ? detectCreatorVars(headerText, variableType).slice(0, 1) : [];
  const bodyVarNames = detectCreatorVars(bodyText, variableType);

  const headerSamples = headerSampleMapFromExample(header?.example, variableType);
  const bodySamples = bodySampleMapFromExample(body?.example, variableType);

  const headerVariables = headerVarNames.map((name) => ({
    name: String(name),
    sample: headerSamples[String(name)] || "",
  }));

  const bodyVariables = bodyVarNames.map((name) => ({
    name: String(name),
    sample: bodySamples[String(name)] || "",
  }));

  const quickReplyButtons = [];
  const ctaButtons = [];

  const btns = Array.isArray(buttons?.buttons) ? buttons.buttons : [];
  btns.forEach((btn, index) => {
    const type = String(btn?.type || "").toUpperCase();
    if (type === "QUICK_REPLY") {
      quickReplyButtons.push({
        id: creatorId("qr", index),
        text: btn?.text || "",
      });
      return;
    }

    if (type === "URL") {
      const isDynamic = Array.isArray(btn?.example) && btn.example.length > 0;
      ctaButtons.push({
        id: creatorId("cta", index),
        type: "URL",
        text: btn?.text || "",
        url: btn?.url || "",
        url_type: isDynamic ? "dynamic" : "static",
        urlVariableSample: isDynamic ? String(btn.example[0] || "") : "",
      });
      return;
    }

    if (type === "PHONE_NUMBER") {
      const { country_code, phone_number } = splitE164(btn?.phone_number || "");
      ctaButtons.push({
        id: creatorId("cta", index),
        type: "PHONE_NUMBER",
        text: btn?.text || "",
        country_code,
        phone_number,
      });
      return;
    }

    if (type === "COPY_CODE") {
      ctaButtons.push({
        id: creatorId("cta", index),
        type: "COPY_OFFER_CODE",
        text: "Copy offer code",
        offer_code: typeof btn?.example === "string" ? btn.example : "",
      });
      return;
    }

    if (type === "FLOW") {
      ctaButtons.push({
        id: creatorId("cta", index),
        type: "FLOW",
        text: btn?.text || "",
        flow_id: btn?.flow_id || "",
      });
    }
  });

  const category = (duplicateData?.category || templateJson?.category || "MARKETING").toUpperCase();
  const subtype = category === "AUTHENTICATION" ? "one_time_passcode" : "default";

  const headerMediaUrl = duplicateData?.header_media_url || null;
  const headerFilePreview =
    mediaSample !== "none" && mediaSample !== "location" && headerMediaUrl
      ? headerMediaUrl
      : null;

  return {
    name: duplicateData?.name || `copy_${duplicateData?.originalName || ""}`,
    language: duplicateData?.language || templateJson?.language || "en_US",
    category,
    subtype,
    variableType,
    mediaSample,
    headerText,
    headerVariables,
    body: bodyText,
    bodyVariables,
    footer: footer?.text || "",
    ctaButtons,
    quickReplyButtons,
    headerFile: null,
    headerFilePreview,
    headerMediaUrl,
  };
};

/**
 * Transform AI generated template to component format
 */
export const transformAiGeneratedTemplate = (generated) => {
  const components = [];
  const baseTime = Date.now();

  // Process body text - renumber variables to be sequential (1, 2, 3...)
  let bodyText = generated.body || "";
  const { text: renumberedBodyText, renumberMap: bodyRenumberMap } = renumberVariables(bodyText, 10);
  bodyText = renumberedBodyText;

  // Process header text - renumber variables to be sequential (1 only for header)
  let headerText = generated.headerText || "";
  let headerRenumberMap = {};
  if (generated.headerFormat === "TEXT" && headerText) {
    const headerResult = renumberVariables(headerText, 1);
    headerText = headerResult.text;
    headerRenumberMap = headerResult.renumberMap;
  }

  // HEADER component
  if (generated.headerFormat && generated.headerFormat !== "NONE") {
    const headerComponent = {
      type: "HEADER",
      format: generated.headerFormat,
    };
    if (headerText) {
      headerComponent.text = headerText;
    }
    components.push(headerComponent);
  }

  // BODY component
  components.push({
    type: "BODY",
    text: bodyText,
  });

  // FOOTER component
  if (generated.footer) {
    components.push({
      type: "FOOTER",
      text: generated.footer,
    });
  }

  // BUTTONS component with URL variable extraction
  const buttons = (generated.buttons || []).map((btn, index) => {
    const buttonId = baseTime + 2000 + index;
    let buttonUrl = btn.url || undefined;
    let urlVariable = undefined;

    // Extract and renumber URL variable if URL contains {{variable}}
    if (btn.type === "URL" && buttonUrl) {
      const urlVariables = (buttonUrl || "").match(/\{\{(\d+)\}\}/g) || [];
      if (urlVariables.length === 1) {
        const varMatch = urlVariables[0].match(/\{\{(\d+)\}\}/);
        if (varMatch) {
          const oldVarNum = varMatch[1];
          // Renumber URL variable using body renumber map (URL variables are body variables)
          const newVarNum = bodyRenumberMap[oldVarNum] || oldVarNum;
          buttonUrl = buttonUrl.replace(`{{${oldVarNum}}}`, `{{${newVarNum}}}`);
          
          const sample = Array.isArray(btn.example) && btn.example.length > 0
            ? btn.example[0]
            : generated.variableSamples?.[`body_${oldVarNum}`] || "";
          urlVariable = {
            name: newVarNum.toString(),
            sample: sample,
          };
        }
      }
    }

    const buttonObj = {
      id: buttonId,
      type: btn.type,
      section: btn.type === "QUICK_REPLY" ? "QUICK_REPLY" : "CTA",
      text: btn.text || "",
      url: buttonUrl,
      phone_number: btn.phone_number || undefined,
      example: btn.example || undefined,
    };

    if (urlVariable) {
      buttonObj.urlVariable = urlVariable;
    }

    return buttonObj;
  });

  if (buttons.length > 0) {
    components.push({
      type: "BUTTONS",
      buttons: buttons,
    });
  }

  // Extract variables from renumbered body text (now sequential: 1, 2, 3...)
  const bodyVariableNumbers = extractVariableNumbers(bodyText);
  const bodyVariables = bodyVariableNumbers.map((varNum, index) => {
    // Find original variable number to get sample value
    // If renumbering happened, find the original number; otherwise use current number
    let originalVarNum = varNum.toString();
    if (Object.keys(bodyRenumberMap).length > 0) {
      const found = Object.keys(bodyRenumberMap).find(
        key => bodyRenumberMap[key] === varNum
      );
      if (found) {
        originalVarNum = found;
      }
    }
    
    return {
      id: baseTime + index,
      name: varNum.toString(),
      sample: generated.variableSamples?.[`body_${originalVarNum}`] || "",
      target: "body",
    };
  });

  // Extract variables from renumbered header text
  const headerVariables = [];
  if (generated.headerFormat === "TEXT" && headerText) {
    const headerVariableNumbers = extractVariableNumbers(headerText);
    headerVariableNumbers.forEach((varNum, index) => {
      // Find original variable number to get sample value
      let originalVarNum = varNum.toString();
      if (Object.keys(headerRenumberMap).length > 0) {
        const found = Object.keys(headerRenumberMap).find(
          key => headerRenumberMap[key] === varNum
        );
        if (found) {
          originalVarNum = found;
        }
      }
      
      headerVariables.push({
        id: baseTime + 1000 + index,
        name: varNum.toString(),
        sample: generated.variableSamples?.[`header_${originalVarNum}`] || "",
        target: "header",
      });
    });
  }

  return {
    components,
    variables: [...bodyVariables, ...headerVariables],
  };
};

